import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Drawer, GlobalStyles, IconButton, Stack, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { livyConfig } from 'src/lib/livyTransport';
import { LivyPanel } from './LivyPanel';
import { LivyAvatar } from './LivyAvatar';
import { selectedCompanyName, useLivyChat } from './useLivyChat';
import { derivePageContext } from './pageContext';
import { useResetOnCompanyChange } from './useResetOnCompanyChange';
import { LivyTheme } from './LivyTheme';
import { freshToken, livyJson, tokenForCompany } from 'src/lib/livyApi';
import { canUseLivy } from './access';
import { LivyHistory } from './LivyHistory';
import { isReplayConversation } from './history';
import { LivyPreviewBanner, LivyResidentBanner, ResidentEmptyState, residentPin } from './LivyPreviewBanner';
import { LivySurfacePicker, SURFACE_STARTERS, isPreviewSurface, runSurface, surfaceFromRun } from './LivySurfacePicker';
import { LivyHelp } from './LivyHelp';
import { LivyReports } from './LivyReports';
import { useSupportChat } from './useCapabilities';
import { pageHelpFor, popularReports } from './pageHelp';
import { useBillingType } from './billingType';
import { livyEvent, resetEngagement, useEngagement } from './LivyGreeting';
const STARTERS = ['Explain what I am looking at', 'Why does this show $0.00?', 'What changed since last cycle?'];
const DISCLOSURE_KEY = 'livy.disclosure.v1';

/** Docked width on tablet and up; on a phone the panel covers the screen instead. */
const DOCK_WIDTH = 460;
const disclosureSeen = () => {
  try {
    return localStorage.getItem(DISCLOSURE_KEY) === '1';
  } catch (_unused) {
    return false;
  }
};

/**
 * The same engine as the full page, in a drawer that knows the route it was opened from. Page vs
 * drawer is a shell difference only, which is why there is one useLivyChat and not two.
 */
// An observer so a customer type that loads (or is changed) while the drawer is open re-filters the reports.
const LivyDrawerBody = observer(function LivyDrawerBody(_ref) {
  let open = _ref.open,
    onClose = _ref.onClose,
    pendingAsk = _ref.pendingAsk,
    onAsked = _ref.onAsked,
    pendingHelp = _ref.pendingHelp,
    onHelped = _ref.onHelped;
  const location = useLocation();
  // Not read from storage: nothing in the app writes a company id there, so this was always null
  // and the drawer quietly sent no company at all. The server derives it from the verified token,
  // which is the only trustworthy source anyway.
  const companyLabel = selectedCompanyName();
  const context = useMemo(() => derivePageContext(location.pathname, location.search), [location.pathname, location.search]);

  // Customers answer on their own surface; the proxy enforces this whatever is sent. Staff can switch
  // to it to see exactly what a PM at the selected company gets.
  const staff = canUseLivy();
  const _useState = useState('admin'),
    _useState2 = _slicedToArray(_useState, 2),
    view = _useState2[0],
    setView = _useState2[1];
  const asPm = staff && isPreviewSurface(view) && view !== 'resident-support';
  const asResident = staff && view === 'resident-support';
  // freshToken, not the raw store: a streaming turn never passes through the app's refresh
  // interceptor. Customer and resident previews also move the token onto the company the app has selected.
  const previewing = staff && isPreviewSurface(view);
  const getToken = useCallback(() => previewing ? tokenForCompany() : freshToken(), [previewing]);
  const customerView = !staff || asPm;
  const resident = useMemo(() => residentPin(asResident, context), [asResident, context]);
  // Cached per login, so this shares the launcher's request.
  const engagement = useEngagement(customerView);
  // Staff Admin runs on the page-aware drawer surface; every other view is the page's own surface.
  // A resumed thread keeps the surface it ran on (the page's admin is not the drawer's) until the
  // view changes or a new chat starts.
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    resumedOn = _useState4[0],
    setResumedOn = _useState4[1];
  const surface = !staff ? 'pm' : resumedOn !== null && resumedOn !== void 0 ? resumedOn : view === 'admin' ? 'drawer' : view;
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    historyOpen = _useState6[0],
    setHistoryOpen = _useState6[1];
  const _useState7 = useState('chat'),
    _useState8 = _slicedToArray(_useState7, 2),
    tab = _useState8[0],
    setTab = _useState8[1];
  // Sent after the reset has rendered: the send from before the reset still carries the old thread and history.
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    queuedReport = _useState0[0],
    setQueuedReport = _useState0[1];
  const _useState1 = useState(''),
    _useState10 = _slicedToArray(_useState1, 2),
    helpQuery = _useState10[0],
    setHelpQuery = _useState10[1];
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    helpArticle = _useState12[0],
    setHelpArticle = _useState12[1];
  // Remounts the Help tab per request, so an article left open never answers the next one.
  const _useState13 = useState(0),
    _useState14 = _slicedToArray(_useState13, 2),
    helpRequest = _useState14[0],
    setHelpRequest = _useState14[1];
  const pageHelp = pageHelpFor(location.pathname);
  const billingType = useBillingType();
  const supportChat = useSupportChat(surface, open && customerView && tab === 'help');
  const _useState15 = useState(disclosureSeen),
    _useState16 = _slicedToArray(_useState15, 2),
    disclosed = _useState16[0],
    setDisclosed = _useState16[1];
  const handleDisclosureClose = () => {
    setDisclosed(true);
    try {
      localStorage.setItem(DISCLOSURE_KEY, '1');
    } catch (_unused2) {
      /* a blocked store only means the note shows again */
    }
  };
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('sm'));
  const docked = open && wide;
  const config = livyConfig();
  const _useLivyChat = useLivyChat({
      config: config !== null && config !== void 0 ? config : {
        endpoint: '',
        region: '',
        identityPoolId: '',
        userPoolId: ''
      },
      getToken,
      context,
      surface,
      billingType,
      resident
    }),
    messages = _useLivyChat.messages,
    streaming = _useLivyChat.streaming,
    error = _useLivyChat.error,
    send = _useLivyChat.send,
    stop = _useLivyChat.stop,
    retry = _useLivyChat.retry,
    reset = _useLivyChat.reset,
    resume = _useLivyChat.resume,
    conversationId = _useLivyChat.conversationId,
    live = _useLivyChat.live,
    startLive = _useLivyChat.startLive;
  useResetOnCompanyChange(reset);
  // A resident thread belongs to one resident: moving to another resident's page starts a new chat.
  const pinnedResidency = resident === null || resident === void 0 ? void 0 : resident.residencyId;
  const _useState17 = useState(pinnedResidency),
    _useState18 = _slicedToArray(_useState17, 2),
    chatResidency = _useState18[0],
    setChatResidency = _useState18[1];
  useEffect(() => {
    // Only a record counts: stepping off and back onto the same resident keeps the chat.
    if (!pinnedResidency || pinnedResidency === chatResidency) return;
    setChatResidency(pinnedResidency);
    if (asResident && pinnedResidency) {
      setResumedOn(null);
      reset();
    }
  }, [asResident, pinnedResidency, chatResidency, reset]);
  // A resident thread continues only on its own resident's page: off a record, or resumed from history (whose
  // resident the drawer cannot know), it is the transcript alone, and an ask from elsewhere starts a new chat.
  // So is a kept backtest replay, on any view: the proxy refuses to continue one.
  const readOnly = isReplayConversation(conversationId) || asResident && (!resident || resumedOn === 'resident-support');
  const ask = useCallback(text => {
    if (!readOnly) return void send(text);
    if (asResident && !resident) return; // off a record there is no resident to ask as: the empty state says where to go
    setResumedOn(null);
    reset();
    setQueuedReport(text);
  }, [readOnly, asResident, resident, send, reset]);
  useEffect(() => {
    if (!queuedReport || messages.length > 0) return;
    setQueuedReport(null);
    void send(queuedReport);
  }, [queuedReport, messages.length, send]);
  useEffect(() => {
    if (!open || pendingAsk === null || pendingAsk === undefined || streaming) return;
    setTab('chat');
    if (pendingAsk) ask(pendingAsk);
    onAsked === null || onAsked === void 0 || onAsked();
  }, [open, pendingAsk, streaming, ask, onAsked]);
  useEffect(() => {
    if (!open || pendingHelp === null || pendingHelp === undefined || streaming) return;
    const request = typeof pendingHelp === 'string' ? {
      topic: pendingHelp,
      article: ''
    } : pendingHelp;
    setHelpQuery(request.topic);
    setHelpArticle(request.article);
    setHelpRequest(n => n + 1);
    setTab('help');
    onHelped === null || onHelped === void 0 || onHelped();
  }, [open, pendingHelp, streaming, onHelped]);
  if (!config) return null;
  return (
    /*#__PURE__*/
    // Persistent, not modal: a modal drawer's backdrop and focus trap made the app unclickable
    // while Livy was open. Docked, it narrows the page and the fixed header instead.
    React.createElement(Drawer, {
      anchor: "right",
      variant: "persistent",
      open: open,
      PaperProps: {
        sx: {
          width: {
            xs: '100%',
            sm: DOCK_WIDTH
          },
          boxShadow: 6
        }
      }
    }, docked && /*#__PURE__*/React.createElement(GlobalStyles
    // !important because LivyTheme's style cache is prepended, so the app's own body and
    // AppBar rules would otherwise win.
    , {
      styles: {
        body: {
          marginRight: "".concat(DOCK_WIDTH, "px !important")
        },
        '.MuiAppBar-positionFixed': {
          width: "calc(100% - ".concat(DOCK_WIDTH, "px) !important"),
          right: "".concat(DOCK_WIDTH, "px !important")
        }
      }
    }), /*#__PURE__*/React.createElement(Stack, {
      sx: {
        height: '100%',
        minHeight: 0
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      justifyContent: "space-between",
      sx: {
        px: '16px',
        py: '12px',
        borderBottom: 1,
        borderColor: 'divider'
      }
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      spacing: "10px",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(LivyAvatar, {
      size: 34,
      thinking: streaming
    }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "h6"
    }, "Livy"), /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary"
    }, context.entityType ? "Looking at this ".concat(context.entityType) : 'Looking at this page'))), /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "small",
      disabled: streaming || messages.length === 0,
      onClick: () => {
        setResumedOn(null);
        reset();
      },
      sx: {
        textTransform: 'none'
      }
    }, "New chat"), /*#__PURE__*/React.createElement(Button, {
      size: "small"
      // Resuming swaps the conversation; mid-stream, the reply would land in the wrong one.
      ,
      disabled: streaming,
      onClick: () => setHistoryOpen(true),
      sx: {
        textTransform: 'none'
      }
    }, "History"), /*#__PURE__*/React.createElement(IconButton, {
      onClick: onClose,
      "aria-label": "Close Livy"
    }, "\xD7"))), staff && /*#__PURE__*/React.createElement(Stack, {
      direction: "row",
      alignItems: "center",
      justifyContent: "space-between"
      // Wraps on a phone, where the full-width drawer is narrower than label plus picker.
      ,
      sx: {
        px: '16px',
        py: '6px',
        borderBottom: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
        rowGap: '4px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary"
    }, "View as"), /*#__PURE__*/React.createElement(LivySurfacePicker, {
      value: view,
      disabled: streaming,
      onChange: next => {
        setView(next);
        setResumedOn(null);
        reset();
      }
    })), asPm && /*#__PURE__*/React.createElement(LivyPreviewBanner, {
      companyLabel: companyLabel
    }), resident && /*#__PURE__*/React.createElement(LivyResidentBanner, null), customerView && (asPm || !disclosed) && /*#__PURE__*/React.createElement(Alert, {
      severity: "info",
      icon: false,
      onClose: asPm ? undefined : handleDisclosureClose,
      sx: {
        mx: '16px',
        mt: '10px',
        py: 0,
        borderRadius: '10px',
        '& .MuiAlert-message': {
          fontSize: 13
        }
      }
    }, "Livy is in beta. It reads your account, can't change anything and can be wrong, so check figures on the linked report. Chats are kept for 90 days."), /*#__PURE__*/React.createElement(Tabs, {
      value: tab,
      onChange: (_, next) => setTab(next),
      variant: "fullWidth",
      sx: {
        minHeight: 36,
        borderBottom: 1,
        borderColor: 'divider',
        '& .MuiTab-root': {
          minHeight: 36,
          py: 0,
          textTransform: 'none'
        }
      }
    }, /*#__PURE__*/React.createElement(Tab, {
      value: "chat",
      label: "Chat"
    }), /*#__PURE__*/React.createElement(Tab, {
      value: "help",
      label: "Help",
      disabled: streaming
    }), !asResident && /*#__PURE__*/React.createElement(Tab, {
      value: "reports",
      label: "Reports",
      disabled: streaming
    })), tab === 'reports' && !asResident ? /*#__PURE__*/React.createElement(LivyReports, {
      billingType: billingType(),
      favorites: engagement === null || engagement === void 0 ? void 0 : engagement.favorites,
      mostRun: engagement === null || engagement === void 0 ? void 0 : engagement.favorite,
      onRun: prompt => {
        // A report is its own conversation, so its answer and any repeat offer are about that report.
        reset();
        setTab('chat');
        setQueuedReport(prompt);
      }
    }) : tab === 'help' ? /*#__PURE__*/React.createElement(LivyHelp, {
      key: helpRequest,
      initialQuery: helpQuery,
      recent: customerView ? engagement === null || engagement === void 0 ? void 0 : engagement.topics : undefined,
      onboarding: customerView,
      billingType: customerView ? billingType() : undefined,
      supportFormUrl: customerView ? engagement === null || engagement === void 0 ? void 0 : engagement.supportFormUrl : undefined,
      supportChat: supportChat,
      memory: customerView ? engagement === null || engagement === void 0 ? void 0 : engagement.memory : undefined,
      onRemoveFact: customerView ? async fact => {
        await livyJson('/engagement/memory', {
          method: 'DELETE',
          body: {
            fact
          }
        });
        resetEngagement();
      } : undefined,
      onForget: customerView ? async () => {
        await livyJson('/engagement', {
          method: 'DELETE'
        });
        resetEngagement();
      } : undefined,
      initialArticle: helpArticle,
      onAsk: text => {
        setTab('chat');
        ask(text);
      }
    }) : asResident && !resident && messages.length === 0 ? /*#__PURE__*/React.createElement(ResidentEmptyState, null) : /*#__PURE__*/React.createElement(LivyPanel, {
      live: live,
      onLive: startLive,
      active: open,
      conversationId: conversationId
      // The same surface the turn runs on. Telling the panel 'pm' while the hook sent
      // 'drawer' fetched the wrong capability groups and filed feedback under a surface
      // nobody was using.
      ,
      surface: surface,
      messages: messages,
      streaming: streaming,
      error: error,
      companyLabel: companyLabel,
      starters: staff && view !== 'admin' ? SURFACE_STARTERS[view] : STARTERS,
      reach: staff && view === 'marketing' ? 'knowledge' : 'account',
      reports: customerView ? popularReports(billingType(), engagement === null || engagement === void 0 ? void 0 : engagement.favorite) : undefined,
      pageHelp: pageHelp === null || pageHelp === void 0 ? void 0 : pageHelp.label,
      onPageHelp: () => {
        var _pageHelp$topic;
        if (pageHelp) void livyEvent('page_help', pageHelp.key);
        setHelpQuery((_pageHelp$topic = pageHelp === null || pageHelp === void 0 ? void 0 : pageHelp.topic) !== null && _pageHelp$topic !== void 0 ? _pageHelp$topic : '');
        setHelpRequest(n => n + 1);
        setTab('help');
      },
      readOnly: readOnly,
      onSend: send,
      onStop: stop,
      onRetry: retry
    })), /*#__PURE__*/React.createElement(LivyHistory, {
      open: historyOpen,
      onClose: () => setHistoryOpen(false),
      onResume: (id, history, ran) => {
        // Back on the view it ran on: a staff thread resumed as Customer would show staff reach as a PM's.
        if (staff) {
          var _residentPin;
          setView(surfaceFromRun(ran));
          setResumedOn(runSurface(ran));
          // The record already open is not a move to another resident, so it must not reset the resumed thread.
          setChatResidency((_residentPin = residentPin(true, context)) === null || _residentPin === void 0 ? void 0 : _residentPin.residencyId);
        }
        resume(id, history);
      }
    }))
  );
});

/** Ripples off inside Livy: see LivyTheme. */
export function LivyDrawer(props) {
  return /*#__PURE__*/React.createElement(LivyTheme, null, /*#__PURE__*/React.createElement(LivyDrawerBody, props));
}